/**
 * MyMouvHome — full listing page /mymouv with month filter.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Ticket, Calendar, MapPin, ArrowLeft } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { getMediaUrl } from '../utils/mediaUrl';

const MONTHS_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const MONTHS_FALLBACK = ['JAN', 'FÉV', 'MAR', 'AVR', 'MAI', 'JUN', 'JUL', 'AOÛ', 'SEP', 'OCT', 'NOV', 'DÉC'];
const currencySymbol = (c) => ({ EUR: '€', USD: '$', GBP: '£', CAD: 'CA$', JPY: '¥', CNY: '¥', INR: '₹', BRL: 'R$', RUB: '₽', KRW: '₩', ARS: '$', XOF: 'FCFA', XAF: 'FCFA' }[c] || c);

const MyMouvHome = () => {
  const { t } = useLanguage();
  const monthLabels = MONTHS_KEYS.map((k, i) => t(`months.short.${k}`, MONTHS_FALLBACK[i]));
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year] = useState(now.getFullYear());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async (m) => {
    setLoading(true);
    try {
      const r = await axios.get(`/api/mymouv/events?month=${m}&year=${year}`);
      setEvents(r.data?.events || []);
    } catch (_e) {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(month); }, [month, load]);

  const minPrice = (ev) => {
    const paid = (ev.ticket_types || []).filter(t => Number(t.price) > 0);
    if (paid.length === 0) return { free: true };
    const m = paid.reduce((a, b) => (Number(a.price) < Number(b.price) ? a : b));
    return { free: false, price: m.price, currency: m.currency };
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="mymouv-home">
      {/* Hero */}
      <div className="relative h-64 md:h-80 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-900/40 via-black/80 to-black" />
        <img src="https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=1600&q=80" alt="concert" className="w-full h-full object-cover opacity-40" />
        <button onClick={() => navigate('/films?tab=studio')} className="absolute top-4 left-4 p-2 rounded-full bg-black/50 hover:bg-black/70">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="absolute bottom-6 left-6 right-6">
          <h1 className="text-4xl md:text-5xl font-bold">{t('mymouvHome.heroTitle1', 'Événements &')} <span className="text-fuchsia-500">{t('mymouvHome.heroTitle2', 'Concerts')}</span></h1>
          <p className="text-sm text-white/70 mt-2">{t('mymouvHome.heroSubtitle', 'Découvrez les meilleurs événements près de chez vous')}</p>
        </div>
      </div>

      {/* Month tabs */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar px-4 py-4 border-b border-white/10">
        {monthLabels.map((m, i) => (
          <button
            key={MONTHS_KEYS[i]}
            onClick={() => setMonth(i + 1)}
            data-testid={`mymouv-month-${i + 1}`}
            className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition ${
              month === i + 1
                ? 'bg-fuchsia-500 text-white'
                : 'bg-white/5 text-white/70 hover:bg-white/10 border border-white/10'
            }`}
          >
            {m}
          </button>
        ))}
      </div>

      <div className="px-4 py-6 max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">{monthLabels[month - 1]} {year}</h2>
          <span className="text-sm text-white/50">{events.length} {t('mymouvHome.eventsCount', 'événement(s)')}</span>
        </div>

        {loading ? (
          <div className="text-sm text-white/50">{t('common.loading', 'Chargement…')}</div>
        ) : events.length === 0 ? (
          <div className="text-center py-16 text-white/40">
            <Ticket className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>{t('mymouvHome.noneMonth', 'Aucun événement ce mois-ci.')}</p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {events.map((ev) => {
              const price = minPrice(ev);
              return (
                <button
                  key={ev.id}
                  onClick={() => navigate(`/mymouv/events/${ev.id}`)}
                  className="rounded-3xl overflow-hidden bg-gradient-to-br from-fuchsia-900/40 to-purple-900/40 border border-fuchsia-500/30 hover:border-fuchsia-400/60 transition text-left"
                  data-testid={`mymouv-list-card-${ev.id}`}
                >
                  <div className="relative h-40 bg-fuchsia-950/60 overflow-hidden">
                    {ev.cover_image_url && (
                      <img src={getMediaUrl(ev.cover_image_url)} alt={ev.title} className="w-full h-full object-cover" />
                    )}
                    <div className="absolute top-3 right-3 px-3 py-1 rounded-full bg-fuchsia-500 text-white text-sm font-bold">
                      {price.free ? t('mymouvHome.free', 'Gratuit') : `${price.price}${currencySymbol(price.currency)}`}
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="text-lg font-bold text-white">{ev.title}</h3>
                    <p className="text-sm text-white/60 line-clamp-2 mt-1">{ev.description}</p>
                    <div className="mt-3 space-y-1 text-sm text-white/70">
                      <div className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-fuchsia-300" /> {ev.event_date}</div>
                      <div className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-fuchsia-300" /> {ev.location?.name || ev.location?.address}</div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-1.5 text-xs text-fuchsia-300">
                      <Ticket className="w-3.5 h-3.5" />
                      {ev.tickets_remaining} {t('mymouvHome.seatsRemaining', 'places restantes')}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyMouvHome;
